import { Listbox, Transition } from "@headlessui/react";
import ChevronDownIcon from "@heroicons/react/24/solid/ChevronDownIcon";
import CheckIcon from "@heroicons/react/24/solid/CheckIcon";
import ArrowLeftIcon from "@heroicons/react/24/outline/ArrowLeftIcon";
import CardMembership from "@mui/icons-material/CardMembership";
import { Timestamp } from "firebase/firestore";
import { DateTime } from "luxon";
import DatePicker from "react-datepicker";
import { useState, Fragment, useEffect, useMemo } from "react";
// Local
import { ItemMembershipData } from "../../models/item-membership-data";
import { ExistingMembershipTemplate } from "../../models/membership-template";
import { SchedulingButton } from "../../Pages/Scheduling/SchedulingContainer";
import BaseInputTextArea from "../BaseInputTextArea";
import { useMembershipTemplatesStore } from "../../store/membership-templates";
import { ExistingMembership } from "../../models/membership";
import { DbRead } from "../../database";
import { logger } from "../../logging";
import MembershipExistsForLineItem from "../Invoices/MembershipExistsForLineItem";
import { buttons } from "../../strings";
import { StyledTooltip } from "../StyledTooltip";

function getReadableStartMethod(startMethod: StartMethodOptions) {
  switch (startMethod) {
    case "sameTask":
      return "Current Task";
    case "uponPaymentReceived":
      return "Upon Payment Received";
    case "futureDate":
      return "Future Date";
    default:
      return "Unknown";
  }
}
const startMethodOptions = [
  "sameTask",
  "uponPaymentReceived",
  "futureDate",
] as const;
type StartMethodOptions = (typeof startMethodOptions)[number];

type EstimateProps = {
  type: "estimate";
  shouldAllowCreate: boolean;
};

type InvoiceProps = {
  type: "invoice";
  siteKey: string;
  /** component returns null if this is true. */
  isMembershipInvoice: boolean;
  /** component will display a link to the membership, instead of allowing membership creation. */
  createdMembershipID: string | undefined;
};

/**
 * Component that allows the user to create memberships from estimate items or
 * invoice line items.
 *
 * If a membership has been created for an invoice line item, component displays
 * MembershipPill (link to membership)
 *
 * Memberships are created from estimate items when the estimate is converted to
 * an invoice, or from invoice line items when the invoice is updated
 */
export default function AddMembershipToItem(
  props: {
    priceBookItemID: string;
    hasTask: boolean;
    membershipData: ItemMembershipData | null;
    setMembershipData: (
      data: React.SetStateAction<ItemMembershipData | null>,
    ) => void;
  } & (EstimateProps | InvoiceProps),
): JSX.Element | null {
  const fullTemplateList = useMembershipTemplatesStore(
    (state) => state.membershipTemplates,
  );
  /** filtered: templates for this item */
  const templates = useMemo(
    () =>
      fullTemplateList.filter(
        (t) => t.priceBookItemID === props.priceBookItemID,
      ),
    [props.priceBookItemID, fullTemplateList],
  );

  const [userChoice, setUserChoice] = useState<"create" | "noCreate" | null>(
    props.membershipData ? "create" : null,
  );
  const [membershipStartMethod, setMembershipStartMethod] =
    useState<StartMethodOptions>(
      getInitialStartMethod(props.hasTask, props.membershipData),
    );
  const [selectedTemplate, setSelectedTemplate] =
    useState<ExistingMembershipTemplate | null>(
      templates.find(
        (t) => t.id === props.membershipData?.membershipTemplateID,
      ) ?? null,
    );

  // for MembershipExistsForLineItem
  const [templateTitle, setTemplateTitle] = useState<string | null>(null);

  const [membershipStates, setMembershipStates] = useState<{
    fetching: boolean;
    errorFetching: boolean;
    doc: ExistingMembership | null;
  }>({
    fetching: false,
    errorFetching: false,
    doc: null,
  });

  const createdMembershipID =
    props.type === "invoice" && props.createdMembershipID;
  const siteKey = props.type === "invoice" && props.siteKey;
  useEffect(() => {
    function getMembership() {
      if (!createdMembershipID) return undefined;
      if (!siteKey) return undefined;

      setMembershipStates((prev) => ({ ...prev, fetching: true }));

      const unsubscribe = DbRead.memberships.subscribe({
        siteKey: siteKey,
        membershipDocID: createdMembershipID,
        onChange: (doc) => {
          setMembershipStates({ doc, fetching: false, errorFetching: false });
          setTemplateTitle(
            templates.find((t) => t.id === doc.membershipTemplateID)?.title ??
              null,
          );
        },
        onError: (error) => {
          setMembershipStates({
            doc: null,
            fetching: false,
            errorFetching: true,
          });
          logger.error(
            `siteKeys/${siteKey}/memberships/${createdMembershipID}: Firestore error: ${error.code} ${JSON.stringify(error.cause)}`,
            error,
          );
        },
      });
      return unsubscribe;
    }
    const unsubscribeFn = getMembership();
    return () => unsubscribeFn && unsubscribeFn();
  }, [createdMembershipID, siteKey, templates]);

  // when there's multiple templates
  function handleSelectTemplate(template: ExistingMembershipTemplate) {
    setSelectedTemplate(template);
    props.setMembershipData((prev) => {
      if (prev) return { ...prev, membershipTemplateID: template.id };
      return {
        membershipTemplateID: template.id,
        isSameTask: props.hasTask,
        selectedStartDate: null,
        notes: null,
      };
    });
  }

  function updateNotes(ev: React.ChangeEvent<HTMLTextAreaElement>) {
    if (!selectedTemplate) return;
    const newNote = ev.target.value.length > 0 ? ev.target.value : null;
    props.setMembershipData((prev) => {
      if (prev) return { ...prev, notes: newNote };
      return {
        membershipTemplateID: selectedTemplate?.id,
        isSameTask: false,
        selectedStartDate: null,
        notes: newNote,
      };
    });
  }

  function handleChoiceYes(): void {
    setUserChoice("create");
    if (templates.length !== 1) return; // if there's more than one, they have to select

    setSelectedTemplate(templates[0]);
    props.setMembershipData({
      membershipTemplateID: templates[0].id,
      isSameTask: props.hasTask,
      selectedStartDate: null,
      notes: null,
    });
  }

  function handleChoiceNo(): void {
    setUserChoice("noCreate");
    props.setMembershipData(null);
    setSelectedTemplate(null);
  }

  function handleBackToChoices(): void {
    setUserChoice(null);
    setSelectedTemplate(null);
    props.setMembershipData(null);
  }

  if (templates.length === 0) return null;
  if (props.type === "invoice" && props.isMembershipInvoice) return null;
  if (props.type === "estimate" && !props.shouldAllowCreate) return null;

  /*
  removed/replaced cus if the user is editing an estimate, we can't predict with
  100% certainty if the user is editing an estimateItem or is adding a new one.
  shouldn't confuse them with something that might not be true..

  if (props.type === "estimate" && !props.shouldAllowCreate) {
    return (
      <p className="-mt-2 italic text-red-700">
        Note: If a membership item is added to this estimate, no membership will
        be created. Add the membership line item to the invoice in order to
        create a membership.
      </p>
    );
  }
  */

  if (createdMembershipID) {
    return (
      <MembershipExistsForLineItem
        membership={membershipStates.doc}
        templateTitle={templateTitle ?? "[Unknown Template]"}
        fetching={membershipStates.fetching}
        errorFetching={membershipStates.errorFetching}
      />
    );
  }

  // displayed after a template is selected
  const FieldsJSX = () => {
    if (!selectedTemplate) return null;
    return (
      <>
        <div className="flex flex-col items-start justify-end gap-2 md:flex-row">
          {props.hasTask && (
            <span>
              Does the membership start with the task on this current{" "}
              {props.type}, or will it start at a date in the future?
            </span>
          )}
          {!props.hasTask && (
            <span>
              To set a membership start date in the future, choose "future date"
              in the dropdown menu, and then select the date.
            </span>
          )}
          <div className="flex flex-col gap-2 lg:flex-row">
            <div className="z-10">
              <Listbox
                onChange={(event) => {
                  if (
                    event !== "futureDate" &&
                    event !== "sameTask" &&
                    event !== "uponPaymentReceived"
                  ) {
                    throw new Error("Invalid start method");
                  }
                  setMembershipStartMethod(event);

                  // if they don't select a date after choosing "future date"
                  // in the dropdown, we want to assign today's date
                  const startDate =
                    event === "futureDate" ? Timestamp.now() : null;

                  props.setMembershipData((prev) => {
                    if (prev) {
                      return {
                        ...prev,
                        isSameTask: event === "sameTask",
                        selectedStartDate: startDate,
                      };
                    }
                    return {
                      membershipTemplateID: selectedTemplate.id,
                      isSameTask: event === "sameTask",
                      selectedStartDate: startDate,
                      notes: null,
                    };
                  });
                }}
              >
                <div className="relative">
                  <Listbox.Button className="relative h-10 w-60 cursor-pointer rounded-md border border-primary bg-white py-2 pl-3 pr-10 text-left outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-primaryLight">
                    <span className="block truncate">
                      {getReadableStartMethod(membershipStartMethod)}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon
                        className="h-5 w-5 text-gray-700"
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute max-h-60 w-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                      {props.hasTask && (
                        <Listbox.Option
                          key="membershipCurrentTask"
                          className={({ active, selected }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active || selected
                                ? "bg-primaryOpacity90 text-primary"
                                : "text-gray-700"
                            }`
                          }
                          value="sameTask"
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-medium" : "font-normal"
                                }`}
                              >
                                Current Task
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                                  <CheckIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      )}
                      <Listbox.Option
                        key="membershipUponPaymentReceived"
                        className={({ active, selected }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active || selected
                              ? "bg-primaryOpacity90 text-primary"
                              : "text-gray-700"
                          }`
                        }
                        value="uponPaymentReceived"
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? "font-medium" : "font-normal"
                              }`}
                            >
                              Upon Payment Received
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                      <Listbox.Option
                        key="membershipFutureDate"
                        className={({ active, selected }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active || selected
                              ? "bg-primaryOpacity90 text-primary"
                              : "text-gray-700"
                          }`
                        }
                        value="futureDate"
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? "font-medium" : "font-normal"
                              }`}
                            >
                              Future Date
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
            {membershipStartMethod === "futureDate" ? (
              <DatePicker
                selected={props.membershipData?.selectedStartDate?.toDate()}
                onChange={(date: Date) => {
                  props.setMembershipData((prev) => {
                    if (prev) {
                      return {
                        ...prev,
                        selectedStartDate: Timestamp.fromDate(date),
                      };
                    }
                    return {
                      membershipTemplateID: selectedTemplate.id,
                      isSameTask: false,
                      selectedStartDate: Timestamp.fromDate(date),
                      notes: null,
                    };
                  });
                }}
                showTimeSelect={false}
                shouldCloseOnSelect={true}
                customInput={<SchedulingButton />}
                minDate={DateTime.now().minus({ year: 1 }).toJSDate()}
              />
            ) : null}
          </div>
        </div>

        {/* struggled to get this textarea input to not blur after each keystroke.
        making the input uncontrolled and using onBlur fixed it */}
        <BaseInputTextArea
          //   ref={textareaRef}
          text="Membership Notes"
          inputName="membershipNotes"
          admin={true}
          required={false}
          rows={2}
          className="mt-6 w-full"
          overrideLabelBG="bg-gradient-to-b from-blue-50 from-10% to-white"
          defaultValue={props.membershipData?.notes ?? ""}
          onBlur={updateNotes}
        />
      </>
    );
  };

  // displayed when user wants to create a new membership
  const CreateJSX = () => {
    return (
      <>
        <div className="ml-8 flex flex-row items-center gap-2">
          <StyledTooltip title="Back to choices">
            <button
              onClick={handleBackToChoices}
              className="rounded-full border border-blue-800 bg-white p-1 text-blue-900 transition-colors hover:bg-blue-100"
            >
              <ArrowLeftIcon className="h-5 w-5 stroke-2" />
            </button>
          </StyledTooltip>
          {selectedTemplate ? (
            <p className="mt-1 text-gray-900">
              The {selectedTemplate.title} membership will be created when
              {props.type === "estimate"
                ? " this estimate is converted to an invoice."
                : " the line items are saved."}
            </p>
          ) : (
            <p className="mt-1">Select a Template</p>
          )}
        </div>

        <div className="my-2 h-px w-full bg-blue-300" />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {templates.length > 1 && (
            <span className="flex flex-row flex-wrap gap-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleSelectTemplate(t)}
                  disabled={selectedTemplate?.id === t.id}
                  className={`max-w-60 truncate rounded-full border px-3 py-0.5 font-medium sm:max-w-80 ${
                    selectedTemplate?.id === t.id
                      ? "border-blue-800 bg-blue-200"
                      : "border-gray-300 bg-gray-200 transition-colors hover:bg-blue-100"
                  }`}
                >
                  {t.title}
                </button>
              ))}
            </span>
          )}
        </div>
        {templates.length > 1 && selectedTemplate && (
          <div className="my-2 h-px w-full bg-blue-300" />
        )}

        {selectedTemplate && <FieldsJSX />}
      </>
    );
  };

  // displayed when user does not want to create a new membership
  const NoCreateJSX = () => {
    return (
      <div className="ml-9 flex flex-row items-center gap-2">
        <StyledTooltip title="Back to choices">
          <button
            onClick={handleBackToChoices}
            className="rounded-full border border-blue-800 bg-white p-1 text-blue-900 transition-colors hover:bg-blue-100"
          >
            <ArrowLeftIcon className="h-5 w-5 stroke-2" />
          </button>
        </StyledTooltip>
        <p>No membership will be created for this item.</p>
      </div>
    );
  };

  // displayed when user has not yet made a choice
  const InitialJSX = () => {
    return (
      <>
        <p className="ml-9">
          {templates.length === 1
            ? `This item is associated with the ${templates[0].title} membership template.`
            : `This item is associated with multiple membership templates.`}
        </p>
        <p className="my-1 text-center text-base text-gray-900">
          Do you want to create a new membership from this{" "}
          {props.type === "estimate" ? "estimate" : "line"} item?
        </p>
        <div className="flex flex-row items-center justify-center gap-2">
          <button
            onClick={handleChoiceYes}
            className="rounded-md border border-blue-800 bg-white px-2.5 py-0.5 uppercase text-blue-800 transition-colors hover:bg-blue-100"
          >
            {buttons.YES}
          </button>
          <button
            onClick={handleChoiceNo}
            className="rounded-md border border-blue-800 bg-white px-2.5 py-0.5 uppercase text-blue-800 transition-colors hover:bg-blue-100"
          >
            {buttons.NO}
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      <div
        className={`${
          !userChoice || userChoice === "noCreate" ? "max-w-fit" : ""
        } px-4 pt-1 ${!userChoice && "pb-1.5"} ${
          userChoice === "create" && "pb-2"
        } ${userChoice === "noCreate" && "pb-1"} relative rounded-md border border-blue-800 bg-blue-50 text-sm font-medium text-gray-600`}
      >
        <span className="absolute -top-2.5 left-2 rounded-full border border-blue-800 bg-blue-200 p-1 text-blue-900">
          <CardMembership sx={{ fontSize: "1.4rem", opacity: 0.8 }} />
        </span>

        {!userChoice && <InitialJSX />}
        {userChoice === "create" && <CreateJSX />}
        {userChoice === "noCreate" && <NoCreateJSX />}
      </div>
    </>
  );
}

function getInitialStartMethod(
  hasTask: boolean,
  membershipData: ItemMembershipData | null,
): StartMethodOptions {
  if (!membershipData) return hasTask ? "sameTask" : "uponPaymentReceived";

  if (membershipData.isSameTask) return "sameTask";
  if (membershipData.selectedStartDate) return "futureDate";
  return "uponPaymentReceived";
}
